// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTaskSummary } from '../api/tasks';
import type { TaskSummary } from '../types';

interface SummaryCard {
  label: string;
  key: keyof TaskSummary;
  colorClass: string;
  textColorClass: string;
}

const summaryCards: SummaryCard[] = [
  {
    label: 'Pending',
    key: 'pending',
    colorClass: 'bg-blue-50 border-blue-100',
    textColorClass: 'text-blue-700',
  },
  {
    label: 'In Progress',
    key: 'inProgress',
    colorClass: 'bg-yellow-50 border-yellow-100',
    textColorClass: 'text-yellow-700',
  },
  {
    label: 'Completed',
    key: 'completed',
    colorClass: 'bg-green-50 border-green-100',
    textColorClass: 'text-green-700',
  },
  {
    label: 'Rejected',
    key: 'rejected',
    colorClass: 'bg-red-50 border-red-100',
    textColorClass: 'text-red-700',
  },
];

export function DashboardPage() {
  const [summary, setSummary] = useState<TaskSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getTaskSummary()
      .then(setSummary)
      .catch(() => setError('Failed to load task summary.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="flex flex-col gap-6 max-w-4xl">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Overview of your current tasks.</p>
      </div>

      {loading && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {summary && !loading && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {summaryCards.map(({ label, key, colorClass, textColorClass }) => (
            <div
              key={key}
              className={`flex flex-col gap-2 rounded-xl border p-4 ${colorClass}`}
            >
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {label}
              </span>
              <span className={`text-3xl font-bold ${textColorClass}`}>
                {summary[key]}
              </span>
            </div>
          ))}
        </div>
      )}

      {summary && !loading && (
        <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-5 py-4">
          <div>
            <span className="text-sm font-medium text-gray-700">Total tasks</span>
            <span className="ml-3 text-lg font-bold text-gray-900">{summary.total}</span>
          </div>
          <Link
            to="/tasks"
            className="text-sm font-medium text-purple-600 hover:text-purple-700"
          >
            View all tasks
          </Link>
        </div>
      )}
    </div>
  );
}
